import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Receipt,
  FileText,
  TrendingDown,
  DollarSign,
  Shield,
  CheckCircle,
  XCircle,
  ChevronRight,
  Sparkles,
  Calculator,
  Loader2,
} from "lucide-react";

const negUrl =
  "https://widget.billshark.com/bundles/freede/prod/v1/negotiation/index.html";
const canUrl =
  "https://widget.billshark.com/bundles/freede/prod/v1/cancellation/index.html";

const LowerBillsPage = () => {
  const [loaded, setLoaded] = useState(false);
  const [frameSource, setFrameSource] = useState(negUrl);
  const [activeTab, setActiveTab] = useState("negotiation");

  const handleLoad = (e) => {
    setLoaded(true);
  };

  const tabData = [
    {
      id: "negotiation",
      title: "Bill Negotiation",
      description: "Lower your monthly bills without changing services",
      icon: TrendingDown,
      url: negUrl,
      color: "from-blue-500 to-cyan-500",
      bgColor: "from-blue-500/10 to-cyan-500/10",
    },
    {
      id: "cancellation",
      title: "Bill Cancellation",
      description: "Cancel unwanted subscriptions and services",
      icon: XCircle,
      url: canUrl,
      color: "from-red-500 to-pink-500",
      bgColor: "from-red-500/10 to-pink-500/10",
    },
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab.id);
    setFrameSource(tab.url);
    setLoaded(false);
  };

  const containerAnimation = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { y: 20, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <div className="p-4 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <motion.div
              initial={{ rotate: -180, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
            >
              <DollarSign className="w-8 h-8 text-green-400" />
            </motion.div>
            <h2 className="text-3xl font-bold text-white">Lower Your Bills</h2>
          </div>
          <p className="text-white/60">
            Save money by negotiating better rates or canceling unnecessary services
          </p>
        </motion.div>

        {/* Tab Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <div className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-2 inline-flex gap-2">
            {tabData.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <motion.button
                  key={tab.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleTabChange(tab)}
                  className={`relative px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                    isActive
                      ? "bg-gradient-to-r " + tab.color + " text-white shadow-lg"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.title}</span>
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-gradient-to-r rounded-xl -z-10"
                      style={{
                        background: `linear-gradient(135deg, ${tab.color.split(" ")[1]} 0%, ${tab.color.split(" ")[3]} 100%)`,
                      }}
                      transition={{ type: "spring", stiffness: 300 }}
                    />
                  )}
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Description for active tab */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6"
        >
          <p className="text-white/60">
            {tabData.find((t) => t.id === activeTab)?.description}
          </p>
        </motion.div>

        {/* iFrame Container */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="relative bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-1 overflow-hidden"
        >
          {/* Loading Indicator */}
          <AnimatePresence>
            {!loaded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-3xl"
              >
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 flex flex-col items-center gap-4">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <Loader2 className="w-8 h-8 text-white" />
                  </motion.div>
                  <p className="text-white font-medium">Loading bill management tools...</p>
                  <p className="text-white/60 text-sm">This may take a few seconds</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* iFrame */}
          <iframe
            title="Lower your bills"
            onLoad={handleLoad}
            src={frameSource}
            className="w-full rounded-2xl"
            style={{
              height: "700px",
              border: "none",
            }}
          />
        </motion.div>

        {/* Bottom Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
              <div>
                <h4 className="text-white font-medium mb-1">How it works</h4>
                <p className="text-white/50 text-sm">
                  Our experts negotiate with your service providers to get you better rates
                  on your existing services. No changes to your service, just lower bills.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <Calculator className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <h4 className="text-white font-medium mb-1">Eligible bills</h4>
                <p className="text-white/50 text-sm">
                  Cable, internet, phone, satellite TV, home security, and more.
                  Upload your bills and we'll handle the rest.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Background Gradient Orbs */}
        <motion.div
          className="fixed top-40 right-20 w-96 h-96 bg-green-500/10 rounded-full blur-3xl pointer-events-none"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="fixed bottom-20 left-10 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"
          animate={{
            x: [0, -30, 0],
            y: [0, 50, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>
    </div>
  );
};

export default LowerBillsPage;
